import type { ProcessedImage } from '@/lib/types';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';

interface EntityVisualProps {
  title: string;
  images?: ProcessedImage[];
  primaryImageId?: string | null;
  altText: string;
  placeholderIcon?: React.ReactNode;
  disableLayout?: boolean;
}

import { ImageLightbox } from '@/components/image-lightbox';
import { SecureImage } from '@/components/secure-image';

/**
 * Shared component for displaying an entity's visual (profile image or placeholder icon).
 * Used in character, location, and organization view pages.
 * Supports a gallery of images with a main preview and thumbnails.
 */
export const EntityVisual = ({
  title,
  images = [],
  primaryImageId,
  altText,
  placeholderIcon = <AccountCircle sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />,
  disableLayout = false
}: EntityVisualProps) => {
  const resolvedPrimaryIndex = primaryImageId
    ? images.findIndex(image => image.id === primaryImageId)
    : -1;
  const [activeIndex, setActiveIndex] = useState(resolvedPrimaryIndex);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Sync the active gallery item when the selected UUID changes.
  useEffect(() => {
    setActiveIndex(resolvedPrimaryIndex);
  }, [resolvedPrimaryIndex]);

  const activeImage = useMemo(() => {
    if (!images || images.length === 0) return null;
    return activeIndex >= 0 ? images[activeIndex] ?? null : null;
  }, [images, activeIndex]);

  const activeImageUrl = activeImage?.profile?.url || activeImage?.original?.url || null;
  const activeImagePath = activeImage?.profile?.path || activeImage?.original?.path || null;
  const activeOriginalUrl = activeImage?.original?.url || null;
  const activeOriginalPath = activeImage?.original?.path || null;

  const content = (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>

      {/* Main Preview */}
      <Box
        onClick={() => activeOriginalUrl && setIsLightboxOpen(true)}
        sx={{
          aspectRatio: '1',
          width: '100%',
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          overflow: 'hidden',
          position: 'relative',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: images.length > 1 ? 2 : 0,
          cursor: activeOriginalUrl ? 'zoom-in' : 'default',
          '&:hover img': { transform: activeOriginalUrl ? 'scale(1.02)' : 'none' },
          '& img': { transition: 'transform 0.3s ease' }
        }}
      >
        {activeImageUrl ? (
          <SecureImage
            src={activeImageUrl}
            path={activeImagePath || undefined}
            alt={altText}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top'
            }}
          />
        ) : (
          placeholderIcon
        )}
      </Box>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <Grid container spacing={1}>
          {images.map((img, idx) => {
            const isSelected = activeIndex === idx;
            const thumbUrl = img.profile?.url || img.original?.url;
            const thumbPath = img.profile?.path || img.original?.path;

            return (
              <Grid size={2.4} key={idx}>
                <Box
                  onClick={() => setActiveIndex(idx)}
                  sx={{
                    aspectRatio: '1',
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    border: 2,
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    cursor: 'pointer',
                    bgcolor: 'action.hover',
                    transition: 'border-color 0.2s',
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'divider'
                    }
                  }}
                >
                  {thumbUrl && (
                    <SecureImage
                      src={thumbUrl}
                      path={thumbPath || undefined}
                      alt={`${altText} thumbnail ${idx + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top'
                      }}
                    />
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );

  const lightbox = (
    <ImageLightbox
      open={isLightboxOpen}
      onClose={() => setIsLightboxOpen(false)}
      imageUrl={activeOriginalUrl}
      imagePath={activeOriginalPath}
      altText={altText}
    />
  );

  if (disableLayout) {
    return (
      <>
        {content}
        {lightbox}
      </>
    );
  }

  return (
    <Grid size={{ xs: 12, lg: 4 }}>
      <Box component="aside" sx={{ position: { lg: "sticky" }, top: 24 }}>
        {content}
      </Box>
      {lightbox}
    </Grid>
  );
};
